/**
 * Pure calculation helper functions shared across derived sub-components.
 * No React, no form hooks — safe to import anywhere.
 */

import {defaultParserForSelects, extractDataGridKey} from "../../dyanimcFormUtil.tsx";
import _ from "lodash";
import {UseFormGetValues} from "react-hook-form";
import {DerivedComponent, FormValues, KeyGridMeta} from "../../DynamicFormTypes.ts";


export function hasValue(val: unknown): boolean {
    if (_.isNil(val)) return false;        // null or undefined
    if (_.isNaN(val)) return false;        // NaN
    return true;                           // 0 returns true
}

export const getStringValue = (fullKey: ReturnType<typeof extractDataGridKey>, key: string | undefined, getValues: UseFormGetValues<FormValues>): string => {
    if (!key) return "";
    const dgValue = getValues(`${fullKey.dataGrid}.${fullKey.row}.${key}`);
    if (hasValue(dgValue)) {
        return getParsedStringValue(`${fullKey.dataGrid}.${fullKey.row}.${key}`, getValues);
    }
    return getParsedStringValue(key, getValues);
};

export const getParsedStringValue = (key: string | undefined, getValues: UseFormGetValues<FormValues>): string => {
    if (!key) return "";
    const raw = getValues(key);
    const str = typeof raw === 'string' ? raw : String(raw ?? '');
    return defaultParserForSelects(str)?.value ?? str;
};

export const findKey = (meta: KeyGridMeta, component: DerivedComponent): string | undefined =>
    component.listFieldKeys.find(f => f.meta === meta)?.fieldKey;


export const getKeyFromDataGridEditedKey = (editedKey: string): string =>
    editedKey.split(".").pop()!;

export const safeParseValue = (value: any): number | undefined => {
    const num = parseFloat(value);
    return isNaN(num) ? undefined : num;
};

export const safeParseValueFromDataGrid = (row: any, key: string): number | undefined => {
    if (row && key in row) {
        const num = parseFloat(row[key]);
        return isNaN(num) ? undefined : num;
    }
    return undefined;
};

export const safeParseDate = (value: any): Date | undefined => {
    if (typeof value === "string") {
        const [y, m, d] = value.trim().split("-").map(Number);
        return new Date(y, m - 1, d);
    }
    return undefined;
};

export const safeParseDateFromDataGrid = (row: any, key: string): Date | undefined => {
    if (row && key in row) {
        if (typeof row[key] === "string") {
            const [y, m, d] = row[key].trim().split("-").map(Number);
            return new Date(y, m - 1, d);
        }
        return undefined;
    }
    return undefined;
};

export const getIntFromKeyName = (allFieldKeys: any[], watchedValues: any[], keyName: string): any => {
    const index = allFieldKeys.findIndex(key => key === keyName);
    return parseInt(watchedValues[index]?.valueOf());
};

export const getDateFromKeyName = (allFieldKeys: any[], watchedValues: any[], keyName: string): any => {
    const index = allFieldKeys.findIndex(key => key === keyName);
    if (watchedValues[index]) {
        return new Date(watchedValues[index]);
    }
    return undefined;
};

export const getSelectValueFromKeyName = (allFieldKeys: any[], watchedValues: any[], keyName: string): any => {
    const index = allFieldKeys.findIndex(key => key === keyName);
    if (watchedValues[index]) {
        return defaultParserForSelects(watchedValues[index]).value;
    }
    return undefined;
};

export const getSelectValueFromGrid = (watchedGrids: any[], gridIndex: number, rowIndex: number, keyName: string): any => {
    const row = watchedGrids[gridIndex]?.[rowIndex];
    if (!row || !row[keyName]) return undefined;
    try {
        const parsed = JSON.parse(row[keyName]);
        return parsed?.value;
    } catch {
        return row[keyName];
    }
};

export const includesIgnoreCase = (value: string, lookFor: string): boolean =>
    value.toLowerCase().includes(lookFor.toLowerCase());

export const roundToNearest = (num: number, multiple: number): number =>
    Math.round(num / multiple) * multiple;

export const noneSelectString = "{\"label\":\"None\",\"value\":\"None\"}";

export const neighborhoodTrendAdjustment = (
    allFieldKeys: any[],
    watchedValues: any[],
    daysElapsed: number,
    oldPrice: number
): number => {
    const propertyValueTrendType: string = getSelectValueFromKeyName(allFieldKeys, watchedValues, "propertyValueTrendType");
    const propertyValueTrendPercent: number = getIntFromKeyName(allFieldKeys, watchedValues, "propertyValueTrendPercent");

    let direction = 0;
    if (propertyValueTrendType === "Declining") direction = -1;
    else if (propertyValueTrendType === "Increasing") direction = 1;

    if (daysElapsed && oldPrice && propertyValueTrendPercent && direction) {
        const percentPerMonth = propertyValueTrendPercent / 100;
        return Math.ceil(percentPerMonth * oldPrice * (daysElapsed / 30) * direction);
    }
    return 0;
};
